import { defineStore } from "pinia";

import companyUsersApi from "@/services/api/companyUsersApi";
import permissionRolesApi from "@/services/api/permissionRolesApi";

const USER_COLORS = [
  "#00bfa5",
  "#667eea",
  "#ff8a3d",
  "#7c3aed",
  "#e53e3e",
  "#38a169",
  "#dd6b20",
  "#3182ce",
];

import { getCompanyId, getUserId } from "@/utils/userUtils";

import { useNotificationStore } from "@/stores/notificationStore";
import { useUserDataStore } from "@/stores/userDataStore";

export const useCompanyUserStore = defineStore("companyUser", {
  state: () => ({
    companyUsers: [],
    companyUsersQueryParams: {},
    counts: { active: null, disabled: null, all: null },
    currentUser: null,
    permissionRoles: [],

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
      from: 0,
      to: 0,
    },

    dataIsLoading: false,
    isSubmitting: false,

    companyId: getCompanyId(),
  }),

  getters: {
    notificationStore() {
      return useNotificationStore();
    },

    getUserColor: () => (userId) => {
      if (!userId) return "#94a3b8";
      return USER_COLORS[userId % USER_COLORS.length];
    },

    currentUserWithRole(state) {
      if (!state.currentUser) return null;

      const role =
        state.permissionRoles.find(
          (r) => r.id === state.currentUser.permission_role_id
        ) || null;

      return {
        ...state.currentUser,
        permission_role: role,
      };
    },
  },

  actions: {
    async fetchCounts() {
      const response = await companyUsersApi.counts(this.companyId);
      this.counts = response.data.result;
    },

    async fetchCompanyUsers(params = {}) {
      this.dataIsLoading = true;
      this.companyUsersQueryParams = params;

      try {
        const response = await companyUsersApi.index(
          this.companyId,
          params
        );

        this.companyUsers = response.data.result;
        this.applyPagination(response.data.pagination_meta);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async loadMoreCompanyUsers() {
      if (
        this.dataIsLoading ||
        this.pagination.page >= this.pagination.pages
      ) {
        return;
      }

      this.dataIsLoading = true;

      try {
        const response = await companyUsersApi.index(this.companyId, {
          ...this.companyUsersQueryParams,
          page: this.pagination.page + 1,
          limit: this.pagination.limit,
        });

        this.companyUsers = [
          ...this.companyUsers,
          ...response.data.result,
        ];
        this.applyPagination(response.data.pagination_meta);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async ensureCompanyUser(userId) {
      const exists = this.companyUsers.some(
        (companyUser) => String(companyUser.user_id) === String(userId)
      );
      if (exists) return;

      const response = await companyUsersApi.index(this.companyId, {
        q: { user: userId },
        limit: 1,
      });
      const companyUser = response.data.result?.[0];

      if (companyUser) {
        this.companyUsers = [companyUser, ...this.companyUsers];
      }
    },

    applyPagination(meta) {
      this.pagination = {
        page: meta.page,
        pages: meta.pages,
        series: meta.series,
        next: meta.next,
        prev: meta.prev,
        count: meta.count,
        limit: meta.limit,
        from: meta.from,
        to: meta.to,
      };
    },

    async fetchUser(userId) {
      this.dataIsLoading = true;

      try {
        const response = await companyUsersApi.show(
          this.companyId,
          userId
        );

        this.currentUser = response.data.result;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchPermissionRoles() {
      try {
        const response = await permissionRolesApi.index(
          this.companyId
        );

        this.permissionRoles = response.data.result;
      } catch (error) {
        console.error("Ошибка загрузки ролей:", error);
      }
    },

    async searchCandidates(search) {
      const response = await companyUsersApi.searchCandidates(
        this.companyId,
        search
      );

      return response.data.result;
    },

    async inviteCandidate(userId, permissionRoleId) {
      this.isSubmitting = true;

      try {
        const response = await companyUsersApi.inviteCandidate(
          this.companyId,
          {
            user_id: userId,
            permission_role_id: permissionRoleId,
          }
        );

        useNotificationStore().showSuccess(
          "Пользователь успешно приглашён в компанию"
        );

        return response.data.result;
      } finally {
        this.isSubmitting = false;
      }
    },

    async inviteNewUser(userParams, permissionRoleId) {
      this.isSubmitting = true;

      try {
        const response = await companyUsersApi.inviteNewUser(
          this.companyId,
          {
            user_params: userParams,
            permission_role_id: permissionRoleId,
          }
        );

        useNotificationStore().showSuccess(
          "Новый пользователь успешно создан и приглашён"
        );

        return response.data.result;
      } finally {
        this.isSubmitting = false;
      }
    },

    async generatePassword() {
      const response = await companyUsersApi.generatePassword(
        this.companyId
      );

      return response.data.result.password;
    },

    async updateUserRole(userId, permissionRoleId) {
      this.isSubmitting = true;

      try {
        const response = await companyUsersApi.changePermissionRole(
          this.companyId,
          userId,
          permissionRoleId
        );

        const updated = response.data.result;

        if (this.currentUser?.id === userId) {
          this.currentUser = updated;
        }

        const idx = this.companyUsers.findIndex((e) => e.id === userId);
        if (idx !== -1) {
          this.companyUsers[idx] = updated;
        }

        if (String(updated.user_id) === String(getUserId())) {
          await useUserDataStore().loadUserData(true);
        }

        useNotificationStore().showSuccess("Роль успешно обновлена");
      } finally {
        this.isSubmitting = false;
      }
    },

    async enableCompanyUser(userId) {
      this.isSubmitting = true;
      try {
        const response = await companyUsersApi.enable(
          this.companyId,
          userId
        );

        if (String(this.currentUser?.id) === String(userId)) {
          this.currentUser = response.data.result;
        }

        const idx = this.companyUsers.findIndex((e) => String(e.id) === String(userId));

        if (idx !== -1) {
          this.companyUsers[idx] = response.data.result;
        }

        useNotificationStore().showSuccess("Пользователь включён");
      } finally {
        this.isSubmitting = false;
      }
    },

    async disableCompanyUser(userId) {
      this.isSubmitting = true;
      try {
        const response = await companyUsersApi.disable(
          this.companyId,
          userId
        );

        if (String(this.currentUser?.id) === String(userId)) {
          this.currentUser = response.data.result;
        }

        const idx = this.companyUsers.findIndex((e) => String(e.id) === String(userId));

        if (idx !== -1) {
          this.companyUsers[idx] = response.data.result;
        }

        useNotificationStore().showSuccess("Пользователь отключён");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
